import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { SELLER_PLAN, PRICE_PER_LOCATION, planLabel, planPrice } from '../pricing';
import { formatCurrency } from '../currencyUtil';

const FEATURES = [
  'Unlimited deal creation',
  'Bid review & acceptance',
  'Transaction tracking',
  'Team member management',
];

export default function Upgrade() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { appUser, setAppUser } = useAuth();

  const planKey = params.get('plan');
  const locationCount = appUser?.account?.location_count || 1;
  const plan = planKey === SELLER_PLAN
    ? { label: planLabel(planKey), features: FEATURES, price: `$${formatCurrency(planPrice(locationCount))}`, period: '/mo' }
    : null;

  const [stage, setStage] = useState('confirm'); // 'confirm' | 'processing' | 'done'
  const [error, setError] = useState('');

  if (!plan) {
    navigate('/settings', { replace: true });
    return null;
  }

  // Only account admins can request a tier change (B3) — the backend enforces this too,
  // but redirecting up front avoids a non-admin filling out the whole flow only to hit
  // a confusing "Admin access required" error after clicking Request.
  if (!appUser?.is_admin) {
    navigate('/settings', { replace: true });
    return null;
  }

  const handleActivate = async () => {
    setError('');
    setStage('processing');
    try {
      const updated = await api.put(`/accounts/${appUser.account_id}`, {
        name: appUser.account.name,
        subscription_tier: planKey,
      });
      setAppUser(u => ({ ...u, account: { ...u.account, ...updated } }));
      setStage('done');
    } catch (err) {
      setError(err.message);
      setStage('confirm');
    }
  };

  if (stage === 'done') {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Request submitted</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-xs">
          Your subscription request is pending admin approval. You'll be notified once it's confirmed.
        </p>
        <button onClick={() => navigate('/buyer/deals', { replace: true })}
          className="mt-6 text-sm text-blue-600 dark:text-blue-400 hover:underline">
          Back to deals
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-10">
      <button onClick={() => navigate('/settings')}
        className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-6 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to settings
      </button>

      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Request Seller Access</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
        A platform admin reviews every request before it goes live. Once approved, your subscription is active for 30 days — contact your account rep to renew.
        Subscriptions are billed by invoice; no card details are collected on the platform.
      </p>

      <div className="card p-6 mb-6">
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-3xl font-bold text-slate-900 dark:text-white">{plan.price}</span>
          <span className="text-sm text-slate-500 dark:text-slate-400">{plan.period}</span>
        </div>
        <p className="text-base font-semibold text-blue-600 dark:text-blue-400 mb-1">{plan.label}</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">
          ${formatCurrency(PRICE_PER_LOCATION)} per location × {locationCount} location{locationCount !== 1 ? 's' : ''}. Unlimited users at each location.
        </p>
        <ul className="space-y-2 mb-6">
          {plan.features.map(f => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
              <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              {f}
            </li>
          ))}
        </ul>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        <button
          onClick={handleActivate}
          disabled={stage === 'processing'}
          className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50"
        >
          {stage === 'processing' ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting…
            </>
          ) : (
            <>Request {plan.label} — {plan.price}/mo</>
          )}
        </button>
      </div>

      <p className="text-xs text-center text-slate-400 dark:text-slate-500">
        Billing is handled manually. You'll be contacted to arrange payment.
      </p>
    </div>
  );
}
